/**
 * 投稿詳細画面の「参考ファイル」表示（読み取り専用）。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-5
 *
 * 投稿者が編集画面で添付した資料 (kind='reference') を、**閲覧者全員** が
 * ダウンロードできるようにする。追加・削除は編集画面側でのみ行う。
 *
 * ★ 宛先指定 (audience==='selected') に依存しない。全員宛の投稿でも
 *   参考資料は読めるべきなので、アクション欄とは独立して描画する。
 */
import { Download, Paperclip } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  formatBytes,
  startDownload,
  useBoardAttachments,
  useResolveAttachmentDownload,
} from '@/hooks/useBoardAttachments';

export function BoardReferenceList({ postId }: { postId: string }) {
  const list = useBoardAttachments(postId);
  const resolveDownload = useResolveAttachmentDownload(postId);

  const refs = (list.data ?? []).filter((a) => a.kind === 'reference');
  if (refs.length === 0) return null;

  const handleDownload = async (attachmentId: string) => {
    try {
      const res = await resolveDownload.mutateAsync({ attachmentId });
      if (res.status === 'ok') startDownload(res.downloadUrl);
      else if (res.status === 'file_missing') toast.error('ファイルが見つかりません');
      else toast.error('ダウンロードできませんでした');
    } catch (err) {
      toast.error(`ダウンロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Paperclip className="size-4 text-[var(--text-secondary)]" />
        <span className="text-xs font-medium text-[var(--text-secondary)]">
          参考ファイル（{refs.length}）
        </span>
      </div>
      {refs.map((a) => (
        <div
          key={a.id}
          className="rounded-md border border-[var(--border)] p-2 flex items-center gap-2"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm text-[var(--text-primary)] truncate">{a.fileName}</div>
            <div className="text-[11px] text-[var(--text-muted)]">
              {formatBytes(a.size)} ・ {a.uploaderName}
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleDownload(a.id)}
            disabled={resolveDownload.isPending}
          >
            <Download className="size-4 mr-1" />
            ダウンロード
          </Button>
        </div>
      ))}
    </div>
  );
}
